
import { useState, useEffect, useRef } from 'react';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export default function ZingBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Hi! I\'m ZingBot, your job search assistant. How can I help you today?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const quickActions = [
    {
      icon: 'ri-home-wifi-line',
      label: 'Find Remote Jobs',
      action: () => handleQuickAction('remote'),
    },
    {
      icon: 'ri-money-dollar-circle-line',
      label: 'High Salary (&gt;$100k)',
      action: () => handleQuickAction('highsalary'),
    },
  ];

  const handleQuickAction = (action: string) => {
    if (action === 'remote') {
      // Navigate to jobs page with remote filter
      const userMessage: Message = {
        id: Date.now().toString(),
        text: 'Show me remote jobs',
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      setTimeout(() => {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Great! I\'m filtering the job list to show only remote positions. Check out the jobs page!',
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);

        // Trigger remote filter on jobs page
        setTimeout(() => {
          if (window.location.pathname === '/jobs') {
            const event = new CustomEvent('zingbot-filter', { detail: { type: 'remote' } });
            window.dispatchEvent(event);
          } else {
            window.REACT_APP_NAVIGATE('/jobs?filter=remote');
          }
        }, 500);
      }, 1000);
    } else if (action === 'highsalary') {
      const userMessage: Message = {
        id: Date.now().toString(),
        text: 'Show me high salary jobs over $100k',
        sender: 'user',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      setTimeout(() => {
        const botMessage: Message = {
          id: (Date.now() + 1).toString(),
          text: 'Perfect! I\'m showing you jobs with salaries over $100k. These are the top-paying positions!',
          sender: 'bot',
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMessage]);

        // Trigger high salary filter
        setTimeout(() => {
          if (window.location.pathname === '/jobs') {
            const event = new CustomEvent('zingbot-filter', { detail: { type: 'highsalary' } });
            window.dispatchEvent(event);
          } else {
            window.REACT_APP_NAVIGATE('/jobs?filter=highsalary');
          }
        }, 500);
      }, 1000);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponse = generateBotResponse(inputValue);
      const botMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
    }, 1500);
  };

  const generateBotResponse = (input: string): string => {
    const lowerInput = input.toLowerCase();

    if (lowerInput.includes('react') || lowerInput.includes('frontend')) {
      return 'I can help you find React and Frontend Developer positions! Use the search bar on the jobs page and type "React" to see all available positions. You can also filter by experience level and salary range.';
    } else if (lowerInput.includes('remote')) {
      return 'Looking for remote work? Great choice! Click the "Find Remote Jobs" button above, or go to the jobs page and use the "Remote" filter in the sidebar.';
    } else if (lowerInput.includes('salary')) {
      return 'To find jobs by salary range, visit the jobs page and use the salary filters in the sidebar. You can filter by ranges like $40k-$80k, $80k-$120k, or $120k+.';
    } else if (lowerInput.includes('apply') || lowerInput.includes('application')) {
      return 'To apply for a job, simply click on any job card to view details, then click the "Apply Now" button. You\'ll need to sign in first. Make sure you have your resume ready!';
    } else if (lowerInput.includes('cv') || lowerInput.includes('resume')) {
      return 'Our AI CV Checker can analyze your resume and match you with the best jobs! Visit the CV Checker page from the navigation menu to get started.';
    } else {
      return 'I\'m here to help you find your dream job! You can ask me about specific roles, remote work, salary ranges, or how to apply. Try using the quick action buttons above for common searches!';
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-br from-[#14B8A6] to-[#8B5CF6] rounded-full shadow-2xl shadow-[#14B8A6]/50 flex items-center justify-center text-white hover:scale-110 transition-transform z-50 cursor-pointer"
      >
        {isOpen ? (
          <i className="ri-close-line text-2xl"></i>
        ) : (
          <i className="ri-robot-2-line text-2xl"></i>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-96 h-[600px] bg-[#0a0a0a]/95 backdrop-blur-xl border border-[#14B8A6]/20 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#14B8A6] to-[#8B5CF6] p-4 flex items-center space-x-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <i className="ri-robot-2-line text-xl text-white"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-white">ZingBot</h3>
              <p className="text-xs text-white/80">Your AI Job Assistant</p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="p-4 bg-white/5 border-b border-white/10">
            <p className="text-xs text-gray-400 mb-2">Quick Actions:</p>
            <div className="flex gap-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={action.action}
                  className="flex-1 px-3 py-2 bg-gradient-to-r from-[#14B8A6]/10 to-[#8B5CF6]/10 border border-[#14B8A6]/20 text-[#14B8A6] text-xs rounded-lg hover:from-[#14B8A6]/20 hover:to-[#8B5CF6]/20 transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className={`${action.icon} mr-1`}></i>
                  <span dangerouslySetInnerHTML={{ __html: action.label }}></span>
                </button>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.sender === 'user'
                      ? 'bg-gradient-to-r from-[#14B8A6] to-[#8B5CF6] text-white'
                      : 'bg-white/10 text-gray-300'
                  }`}
                >
                  {message.sender === 'bot' && (
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-[#14B8A6] to-[#8B5CF6] rounded-full flex items-center justify-center">
                        <i className="ri-robot-2-line text-xs text-white"></i>
                      </div>
                      <span className="text-xs font-semibold text-[#14B8A6]">
                        ZingBot
                      </span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed">{message.text}</p>
                  <p className="text-xs opacity-60 mt-1">
                    {message.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white/10 rounded-2xl px-4 py-3">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-[#14B8A6] rounded-full animate-bounce"
                      style={{ animationDelay: '0.4s' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Ask me anything..."
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#14B8A6] transition-colors text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim()}
                className="w-12 h-12 bg-gradient-to-r from-[#14B8A6] to-[#8B5CF6] rounded-lg flex items-center justify-center text-white hover:shadow-lg hover:shadow-[#14B8A6]/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="ri-send-plane-fill"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
